'use client';

import { RestApiTipIconDto, RestApiTipPresetDto } from '@order-mage/integration-sdk';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithRef } from 'react';
import { type CartTipType } from '@/entities/cart';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { AnimatedPrice } from '@/shared/ui/animated-price';
import { Icon } from '@/shared/ui/icon';
import { Media } from '@/shared/ui/media';

type CartTipBoxAmountBoxIconProps = {
    icons: RestApiTipIconDto[] | null | undefined;
    alt: string;
};
const CartTipBoxAmountBoxIcon = ({ icons, alt }: CartTipBoxAmountBoxIconProps) => {
    const icon = icons?.[0];

    if (!icon?.fullUrl) {
        return null;
    }
    return <Media data={{ url: icon.fullUrl, alt }} width={24} height={24} />;
};

type Props = {
    tipType: CartTipType;
    value: RestApiTipPresetDto['value'];
    label?: RestApiTipPresetDto['label'];
    icons?: RestApiTipIconDto[] | null;
    isActive?: boolean;
    amount?: number;
    currency?: Currency;
} & WithDataAttributes<ComponentPropsWithRef<'button'>>;
export const CartTipBoxAmountBox = ({
    tipType,
    value,
    label,
    icons,
    isActive,
    amount,
    currency,
    className,
    'data-testid': dataTestId,
    'data-type': dataType,
    ...props
}: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const localizedLabel = label ? getLocalizedValue(label) : '';
    const iconAlt = localizedLabel ? `${localizedLabel} tip-icon` : 'tip-icon';

    return (
        <button
            type="button"
            className={cn(
                'cart-tip-box-amount-box',
                isActive && 'cart-tip-box-amount-box--active',
                className,
            )}
            aria-pressed={!!isActive}
            data-testid={dataTestId || 'cart-tip-box-amount-box'}
            data-type={dataType ?? tipType}
            data-selected={isActive}
            {...props}
        >
            {isActive && (
                <span className="cart-tip-box-amount-box__badge" aria-hidden="true">
                    <Icon name="check" />
                </span>
            )}
            <div className="cart-tip-box-amount-box__value" data-testid="cart-tip-box-amount-box-percentage">
                {tipType === 'percentage' && <b>{value}%</b>}
                {tipType === 'fixed' && (
                    <b>
                        {/* `amount` is the roundTip'd value the cart actually charges — showing the
                            raw configured `value` would display 1,99 € while billing 2,00 € (PRICE). */}
                        <AnimatedPrice amount={amount ?? parseFloat(value)} currency={currency as Currency} />
                    </b>
                )}
                {tipType === 'custom' && !!amount && currency && (
                    <b>
                        <AnimatedPrice amount={amount} currency={currency} />
                    </b>
                )}
                <CartTipBoxAmountBoxIcon icons={icons} alt={iconAlt} />
            </div>
            <span className="cart-tip-box-amount-box__amount" data-testid="cart-tip-box-amount-box-amount">
                {tipType === 'percentage' && typeof amount === 'number' && currency ? (
                    <>
                        {localizedLabel && `${localizedLabel} `}(
                        <AnimatedPrice amount={amount} currency={currency} />)
                    </>
                ) : tipType === 'custom' ? (
                    localizedLabel || t('CART_TIP_BOX.CUSTOM_LABEL')
                ) : (
                    localizedLabel || ''
                )}
            </span>
        </button>
    );
};
